import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AgxColumn,
  AgxDropdown,
  AgxRow,
  AgxTextInput,
  AgxTypography,
  Breakpoints,
  useWindowSize,
} from '@urbanx/agx.ui-components2'
import {
  Deposit,
  ConditionType,
  DayType,
  DepositPayable,
  DepositType,
  FormCondition,
  Condition,
  useStaffGetSpecialConditions,
} from '@urbanx/agx.campaigns.client'
import { useSelectedContract } from 'hooks'
import { BusinessDaysOptions } from './BusinessDaysOptions'
import { useFormConditionDateOptions } from './useFormConditionDateOptions'
import { useSpecialConditionsOptions } from './useSpecialConditionsOptions'

interface FormConditionNDaysProps {
  id: string
  fromDateOptions: Condition[]
  defaultValue?: FormCondition | Deposit
  hasPriceOrPercentage?: boolean
  onValueChange: (_condition: FormCondition | Deposit) => void
  validate?: boolean
  conditionTypeOverride?: ConditionType
  depositType?: DepositType
}

const FormConditionNDays = ({
  id,
  fromDateOptions,
  defaultValue,
  hasPriceOrPercentage = false,
  onValueChange,
  validate,
  conditionTypeOverride,
  depositType,
}: FormConditionNDaysProps) => {
  const { contract, campaignId } = useSelectedContract()
  const [value, setValue] = useState<(FormCondition | Deposit) | undefined>(
    () => {
      if (!defaultValue) return defaultValue
      // Initialize dayType if not set - default to Days
      return {
        ...defaultValue,
        dayType: defaultValue.dayType ?? DayType.Days,
      } as FormCondition | Deposit
    }
  )
  const nDaysFromDateOptions = useFormConditionDateOptions(
    fromDateOptions,
    false,
    depositType
  )

  const [nDaysFromDateCondition, setNDaysFromDateCondition] = useState<
    Condition | undefined
  >(() => {
    if (
      defaultValue?.condition === Condition.UponSatisfactionOfSpecialCondition
    ) {
      return Condition.UponSatisfactionOfSpecialCondition
    }

    // Treat AsPerContract as NdaysFromDate for the dropdown
    if (defaultValue?.condition === Condition.AsPerContract) {
      return Condition.NdaysFromDate
    }

    // If defaultValue has a condition, use it
    if (defaultValue?.condition) {
      return defaultValue.condition as Condition
    }

    return (
      (nDaysFromDateOptions.find((option) => option.value as Condition)
        ?.value as Condition) ?? (nDaysFromDateOptions[0]?.value as Condition)
    )
  })

  const windowSize = useWindowSize()
  const isMobile = windowSize === Breakpoints.Mobile

  const { data: specialConditions } = useStaffGetSpecialConditions(
    campaignId ?? '',
    contract?.contractId ?? ''
  )

  useEffect(() => {
    setValue(
      (prev) =>
        ({
          ...prev,
          condition: (nDaysFromDateCondition ?? undefined) as Condition &
            DepositPayable,
          ...(conditionTypeOverride && {
            conditionType: conditionTypeOverride,
          }),
          // If no override and condition is NdaysFromDate, default to Contract type
          // Unless depositType is Balance, then use Unconditional
          ...(!conditionTypeOverride &&
            nDaysFromDateCondition === Condition.NdaysFromDate && {
              conditionType:
                depositType === DepositType.Balance
                  ? ConditionType.Unconditional
                  : ConditionType.Contract,
            }),
        }) as FormCondition | Deposit
    )
  }, [nDaysFromDateCondition, conditionTypeOverride, depositType])

  useEffect(() => {
    // Only update if the current condition is not in the available options
    const isCurrentConditionAvailable = nDaysFromDateOptions.find(
      (option) => option.value === nDaysFromDateCondition
    )

    if (isCurrentConditionAvailable || !nDaysFromDateCondition) return

    // If current condition is not available, select the first valid option
    const updateTo = nDaysFromDateOptions.find((option) => option.value !== '')
      ?.value as Condition

    if (updateTo && updateTo !== nDaysFromDateCondition) {
      setNDaysFromDateCondition(updateTo)
    }
  }, [nDaysFromDateOptions, nDaysFromDateCondition])

  useEffect(() => {
    if (value) {
      onValueChange(value)
    }
  }, [value, onValueChange])

  const specialConditionsOptions = useSpecialConditionsOptions(
    specialConditions?.renderedConditions ?? []
  )

  const selectedSpecialCondition = useMemo(() => {
    if (!value) return ''

    return value?.specialConditionId ?? ''
  }, [value])

  const onSelectedSpecialConditionChange = useCallback(
    (value: string) => {
      const foundId = value === '' ? undefined : value

      setValue((prev) => ({
        ...prev,
        specialConditionId: foundId,
      }))
    },
    [specialConditions]
  )

  return (
    <AgxColumn gap="spacing-lg" fill>
      <AgxRow gap="spacing-md" wrap={isMobile} fill alignItems="alignCenter">
        <AgxRow gap="spacing-md" fill>
          <AgxTextInput
            id={`${id}-NDaysNumberOfDays`}
            type="number"
            hideOptionalText
            maxLength={2}
            stretch
            noHeader
            defaultValue={value?.daysFromDate?.toString()}
            onInputValueChange={({ value }) => {
              setValue((prev) => ({
                ...prev,
                daysFromDate: parseInt(value),
              }))
            }}
            errorText={
              validate && !hasPriceOrPercentage && !value?.daysFromDate
                ? 'Enter days'
                : ''
            }
            hideErrorDescription
          />
        </AgxRow>
        <AgxRow gap="spacing-md" fill>
          <AgxDropdown
            id={`${id}-NDaysDayType`}
            options={BusinessDaysOptions}
            selected={value?.dayType ?? DayType.Days}
            onChange={(value) => {
              setValue((prev) => ({
                ...prev,
                dayType: value as DayType,
              }))
            }}
            hideOptionalText
            noHeader
          />
        </AgxRow>
        <AgxRow
          gap="spacing-md"
          alignItems="alignCenter"
          justifyContent="center"
        >
          <AgxTypography id={`${id}-NDaysFrom`} size="small" type="body_ui">
            from
          </AgxTypography>
        </AgxRow>
      </AgxRow>
      <AgxColumn gap="spacing-md" fill>
        <AgxRow gap="spacing-md" fill>
          <AgxDropdown
            id={`${id}-NDaysConditionFromOptions`}
            options={nDaysFromDateOptions}
            selected={nDaysFromDateCondition ?? ''}
            onChange={(value) => {
              setNDaysFromDateCondition(value as Condition)
            }}
            hideOptionalText
            noHeader
          />
        </AgxRow>
        {nDaysFromDateCondition ===
          Condition.UponSatisfactionOfSpecialCondition && (
          <AgxDropdown
            id={`${id}-SpecialConditionDropdown`}
            options={specialConditionsOptions}
            selected={selectedSpecialCondition}
            onChange={onSelectedSpecialConditionChange}
            hideOptionalText
            required
          />
        )}
      </AgxColumn>
    </AgxColumn>
  )
}

export default FormConditionNDays
